<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">
        {{ magazines.length }} {{ magazines.length === 1 ? 'magazine' : 'magazines' }} · every magazine needs a purchase before download
      </p>
      <button @click="openModal('add')" class="btn-primary">
        <i class="fa-solid fa-plus"></i> Add Magazine
      </button>
    </div>

    <!-- Magazine Table -->
    <div v-if="loadingList" class="admin-empty">Loading magazines...</div>
    <div v-else-if="listError" class="admin-empty text-red-600">{{ listError }}</div>
    <div v-else-if="magazines.length === 0" class="admin-empty">
      <i class="fa-solid fa-book-open text-2xl text-gray-300"></i>
      No magazines found.
    </div>
    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Magazine</th>
            <th>Description</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="mag in magazines" :key="mag.id">
            <td>
              <div class="flex items-center gap-3">
                <img
                  :src="mag.thumbnail_path"
                  :alt="mag.title"
                  class="h-14 w-11 flex-shrink-0 rounded border border-gray-200 bg-gray-50 object-cover"
                />
                <span class="font-medium text-gray-900">{{ mag.title }}</span>
              </div>
            </td>
            <td class="max-w-sm">{{ mag.description || 'No description' }}</td>
            <td>
              <div class="flex flex-wrap justify-end gap-2">
                <button @click="viewPdf(mag)" class="btn-ghost btn-sm">
                  <i class="fa-regular fa-file-pdf"></i> View PDF
                </button>
                <button @click="openModal('update', mag)" class="btn-cream btn-sm">
                  <i class="fa-solid fa-pen"></i> Edit
                </button>
                <button @click="confirmDelete(mag.id)" class="btn-danger btn-sm">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
        <div class="modal-panel">
          <h2 class="mb-6 font-serif text-2xl font-bold text-gray-900">
            {{ modalMode === 'add' ? 'Upload Magazine' : 'Update Magazine' }}
          </h2>

          <form
            @submit.prevent="modalMode === 'add' ? uploadMagazine() : updateMagazine()"
            class="space-y-5"
          >
            <!-- Title -->
            <div>
              <label class="form-label">Title</label>
              <input v-model="title" required placeholder="Magazine title" class="form-input" />
            </div>

            <!-- Description -->
            <div>
              <label class="form-label">Description</label>
              <textarea v-model="description" rows="3" placeholder="Short description" class="form-input resize-none"></textarea>
            </div>

            <!-- PDF -->
            <div>
              <label class="form-label">
                PDF File {{ modalMode === 'add' ? '(required)' : '(optional — leave empty to keep the current PDF)' }}
              </label>
              <input
                ref="pdfInput"
                type="file"
                accept="application/pdf"
                @change="onPDFChange"
                :required="modalMode === 'add'"
                class="file-input"
              />
            </div>

            <!-- Image -->
            <div>
              <label class="form-label">
                Cover Image {{ modalMode === 'add' ? '' : '(optional — leave empty to keep the current cover)' }}
              </label>
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                @change="onImageChange"
                class="file-input"
              />
              <div v-if="imagePreview" class="mt-3 flex items-center gap-3">
                <img :src="imagePreview" alt="Cover preview" class="h-28 w-20 rounded border border-gray-200 object-cover shadow-sm" />
                <p class="text-xs text-gray-500">{{ imageFile ? 'New cover' : 'Current cover' }}</p>
              </div>
            </div>

            <p v-if="error" class="rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">{{ error }}</p>

            <!-- Buttons -->
            <div class="flex gap-3 pt-1">
              <button type="button" @click="closeModal" class="btn-ghost flex-1">Cancel</button>
              <button type="submit" :disabled="loading" class="btn-primary flex-1">
                {{ loading ? 'Saving...' : modalMode === 'add' ? 'Upload' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import api, { errorMessage, openPdf } from '@/api';

export default {
  name: "UploadMagazine",
  data() {
    return {
      title: "",
      description: "",
      pdfFile: null,
      imageFile: null,
      imagePreview: null,
      magazines: [],
      loading: false,
      loadingList: false,
      listError: "",
      error: "",
      showModal: false,
      modalMode: "add",
      selectedMagazineId: null,
    };
  },
  methods: {
    onPDFChange(e) {
      this.pdfFile = e.target.files[0] || null;
    },
    onImageChange(e) {
      this.imageFile = e.target.files[0] || null;
      if (this.imageFile) this.imagePreview = URL.createObjectURL(this.imageFile);
    },

    buildFormData() {
      const formData = new FormData();
      formData.append("title", this.title);
      formData.append("description", this.description || "");
      if (this.pdfFile) formData.append("pdfFile", this.pdfFile);
      if (this.imageFile) formData.append("imageFile", this.imageFile);
      return formData;
    },

    async uploadMagazine() {
      if (!this.pdfFile) {
        this.error = "Please select a PDF file.";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        await api.post('/upload-magazine', this.buildFormData());
        await this.fetchMagazines();
        this.closeModal();
        Swal.fire({ icon: 'success', title: 'Uploaded', text: 'Magazine uploaded successfully', timer: 1800, showConfirmButton: false });
      } catch (err) {
        this.error = errorMessage(err, "Upload failed");
      } finally {
        this.loading = false;
      }
    },

    async updateMagazine() {
      this.loading = true;
      this.error = "";
      try {
        await api.put(`/magazines/${this.selectedMagazineId}`, this.buildFormData());
        await this.fetchMagazines();
        this.closeModal();
        Swal.fire({ icon: 'success', title: 'Saved', text: 'Magazine updated successfully', timer: 1800, showConfirmButton: false });
      } catch (err) {
        this.error = errorMessage(err, "Update failed");
      } finally {
        this.loading = false;
      }
    },

    async fetchMagazines() {
      this.loadingList = true;
      this.listError = "";
      try {
        const res = await api.get('/magazines');
        this.magazines = res.data;
      } catch (err) {
        this.listError = errorMessage(err, 'Failed to load magazines.');
      } finally {
        this.loadingList = false;
      }
    },

    async deleteMagazine(id) {
      try {
        await api.delete(`/magazines/${id}`);
        await this.fetchMagazines();
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Magazine deleted successfully.', timer: 1600, showConfirmButton: false });
      } catch (err) {
        console.error("Error deleting magazine:", err);
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete magazine.'), confirmButtonColor: '#BF3131' });
      }
    },

    async confirmDelete(id) {
      const result = await Swal.fire({
        title: 'Delete Magazine?',
        text: "The PDF and cover image are deleted too. You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DC2626',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await this.deleteMagazine(id);
      }
    },

    async viewPdf(mag) {
      try {
        await openPdf('magazine', mag.id);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Could not open the PDF.'), confirmButtonColor: '#BF3131' });
      }
    },

    openModal(mode, mag = null) {
      this.modalMode = mode;
      this.error = "";
      this.pdfFile = null;
      this.imageFile = null;

      if (mode === "update" && mag) {
        this.selectedMagazineId = mag.id;
        this.title = mag.title;
        this.description = mag.description || "";
        this.imagePreview = mag.thumbnail_path;
      } else {
        this.selectedMagazineId = null;
        this.title = "";
        this.description = "";
        this.imagePreview = null;
      }
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },
  },
  mounted() {
    this.fetchMagazines();
  },
};
</script>
